import { useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate'
import BrokenImageIcon from '@mui/icons-material/BrokenImage'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import { useGetAuthoringComponentQuery } from '../api/authoring'
import type { Child } from '../api/authoring'
import { payloadId, shown } from '../api/useIntent'
import { isImage, thumbnail } from '../model/derived'
import { arrangementsOf, linkIdsIn } from '../model/arrangement'
import { childByLinkId, isLinked, mintLinkId } from '../model/link'
import { acceptsUpload, containable, fieldsSchema, formableIntents, intentArranging, structuralPayload } from '../model/manifest'
import { MediaPicker } from '../ui/MediaPicker'
import { moved, useReorder, type Reorder } from '../ui/reorder'
import { IntentForm } from './IntentForm'
import type { EditorProps } from './types'

/**
 * A gallery, as a grid of the pictures in it.
 *
 * **This is the type that proved intent prose was not enough.** Every
 * intent a Gallery declared was structural — add an image, remove one,
 * reorder them all — so no form could be derived from any of them and the
 * generated editor correctly drew none, printing three intent names over
 * a sentence saying so. That is honest and it is unusable: one customer
 * in three is a photographer, and their site could not be authored in
 * this client at all.
 *
 * So it is hand-written, and it earns that the way `GridEditor` and
 * `FormEditor` do — by doing something a schema cannot describe. A
 * schema says a gallery holds an ordered list of link ids. It cannot say
 * that each one resolves to a picture, that the picture is worth
 * *looking* at, or that the order is the thing being edited.
 *
 * **What is not written here is which intents to send.** They are
 * resolved by role from the manifest, exactly as the composition tree
 * resolves them, so this editor is a *presentation* of an arrangement
 * rather than a second implementation of one. A gallery that renamed its
 * intents tomorrow keeps this grid.
 *
 * **Anything a form can be drawn for is drawn above it**, on the terms
 * `FormEditor` falls through on: the gallery's name is one field, and a
 * hand-written editor that only knew about photos left it unreachable.
 * Nothing here names that intent, so the next field a Gallery gains is
 * reachable the day it is registered.
 */
export function GalleryEditor({
  tenant,
  fields,
  children,
  manifest,
  typeName,
  register,
  busy,
  send,
}: EditorProps) {
  const [adding, setAdding] = useState(false)
  const formable = formableIntents(manifest, typeName, fields)

  // Resolved by role, never named. A gallery keeps one unnamed list, so
  // the intents wanted are the ones that carry no slot.
  const place = intentArranging(manifest, typeName, 'place', false)
  const act = intentArranging(manifest, typeName, 'act', false)
  const arrange = intentArranging(manifest, typeName, 'arrange', false)

  // What the gallery holds and what a picture *is* are two questions with
  // one answer here: the palette says which types may go in, and
  // `acceptsUpload` says which of those hold a file. No type is named to
  // find out.
  const pictureType = containable(manifest, typeName).find((name) => acceptsUpload(manifest, name))

  // The stored order, which is what a reorder must send back whole: an
  // order omitting an entry keeps it, at the end. Read off the type's
  // *fields schema* rather than by naming the field — the schema is what
  // says which property is an arrangement, and the same call is what
  // draws every other type's placements.
  const order = storedOrder(manifest, typeName, fields)
  const reorder = useReorder((_group, next) => {
    if (!arrange) return
    send(arrange.name, structuralPayload(arrange, { order: next })).catch(shown)
  }, !busy)

  const step = (linkId: string, direction: -1 | 1) => {
    const from = order.indexOf(linkId)
    const target = order[from + direction]
    if (!arrange || from < 0 || target === undefined) return
    const next = moved(order, linkId, target)
    if (next) send(arrange.name, structuralPayload(arrange, { order: next })).catch(shown)
  }

  const addImage = (id: string) => {
    if (!place) return
    const taken = new Set(children.map((child) => child.linkId))
    send(
      place.name,
      structuralPayload(place, {
        child: { linkId: mintLinkId(id, taken, 'photo'), id: payloadId(tenant, id) },
        position: order.length,
      }),
    ).catch(shown)
    setAdding(false)
  }

  return (
    <Stack spacing={2}>
      {formable.map((intent) => (
        <IntentForm
          key={intent.name}
          intent={intent}
          fields={fields}
          holder={typeName}
          showName={register === 'build'}
          busy={busy}
          send={send}
        />
      ))}

      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="h3" sx={{ flexGrow: 1 }}>
          Photos
        </Typography>
        {place && pictureType && (
          <Button
            size="small"
            startIcon={<AddPhotoAlternateIcon />}
            disabled={busy}
            onClick={() => setAdding(true)}
          >
            {place.title}
          </Button>
        )}
      </Stack>

      {order.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No photos yet.
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
            gap: 1.5,
          }}
        >
          {order.map((linkId, index) => (
            <Photo
              key={linkId}
              tenant={tenant}
              linkId={linkId}
              child={childByLinkId(children, linkId)}
              first={index === 0}
              last={index === order.length - 1}
              reorder={reorder}
              order={order}
              movable={Boolean(arrange) && order.length > 1}
              onStep={step}
              onRemove={
                act
                  ? () => send(act.name, structuralPayload(act, { link: linkId })).catch(shown)
                  : undefined
              }
              busy={busy}
            />
          ))}
        </Box>
      )}

      {order.length > 1 && arrange && (
        <Typography variant="caption" color="text.secondary">
          Drag a photo to move it. The order here is the order visitors see.
        </Typography>
      )}

      {!place && (
        <Alert severity="info">This gallery does not offer a way to add photos.</Alert>
      )}

      {pictureType && (
        <MediaPicker
          tenant={tenant}
          open={adding}
          typeName={pictureType}
          manifest={manifest}
          onClose={() => setAdding(false)}
          onChoose={(component) => addImage(component.id)}
          busy={busy}
        />
      )}
    </Stack>
  )
}

/** One picture in the grid, read from its own document for the thumbnail. */
function Photo({
  tenant,
  linkId,
  child,
  first,
  last,
  order,
  movable,
  reorder,
  onStep,
  onRemove,
  busy,
}: {
  tenant: string
  linkId: string
  /** Absent where the arrangement names a link id the document has no child for. */
  child: Child | undefined
  first: boolean
  last: boolean
  order: string[]
  movable: boolean
  reorder: Reorder<undefined>
  onStep: (linkId: string, direction: -1 | 1) => void
  onRemove?: () => void
  busy: boolean
}) {
  const targetId = child && isLinked(child) ? child.id : undefined
  const { data } = useGetAuthoringComponentQuery({ tenant, id: targetId ?? '' }, { skip: !targetId })
  const preview = thumbnail(data, 320)
  const drawable = preview && isImage(data)

  const drag = movable ? reorder.rowProps('', linkId, order) : {}
  const landing = movable ? reorder.landingOn('', linkId) : undefined

  return (
    <Paper
      {...drag}
      variant="outlined"
      sx={{
        overflow: 'hidden',
        opacity: movable && reorder.isHeld('', linkId) ? 0.4 : 1,
        borderLeft: landing === 'above' ? 3 : undefined,
        borderRight: landing === 'below' ? 3 : undefined,
        borderColor: landing ? 'primary.main' : undefined,
      }}
    >
      <Box
        sx={{
          height: 96,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'action.hover',
          color: 'text.disabled',
        }}
      >
        {drawable ? (
          <Box component="img" src={preview} alt="" loading="lazy" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <Tooltip title={child ? 'No picture on this one yet.' : 'This photo is missing from the gallery.'}>
            {/* An image, named by the tooltip's words. MUI hides an icon
                from assistive technology by default, which left a broken
                photo announced as nothing. */}
            <BrokenImageIcon fontSize="small" role="img" aria-hidden={false} />
          </Tooltip>
        )}
      </Box>

      <Stack direction="row" alignItems="center" sx={{ px: 0.5, py: 0.25 }}>
        {movable && (
          <Tooltip title="Drag to move it, or use the arrows.">
            <DragIndicatorIcon fontSize="small" sx={{ color: 'text.disabled', cursor: 'grab' }} />
          </Tooltip>
        )}
        {movable && (
          <>
            <IconButton size="small" disabled={busy || first} onClick={() => onStep(linkId, -1)} aria-label="Move earlier">
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" disabled={busy || last} onClick={() => onStep(linkId, 1)} aria-label="Move later">
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {onRemove && (
          <Tooltip title="Take this photo out. The picture itself is kept.">
            <span>
              <IconButton size="small" color="error" disabled={busy} onClick={onRemove}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Stack>
    </Paper>
  )
}

/**
 * The gallery's stored order.
 *
 * **The field is found, not named.** A type's fields schema says which
 * property holds an ordered list of placements, so this asks it — the
 * same question the composition tree asks of every type — rather than
 * reaching for `items`. A gallery that spelled its list differently
 * keeps this grid; a hardcoded name would have made this editor about
 * one type's storage rather than about pictures.
 *
 * It is the order rather than the children because the order *is* the
 * field: the children are a set, and two of them could sit in either
 * sequence.
 */
function storedOrder(
  manifest: EditorProps['manifest'],
  typeName: string,
  fields: Record<string, unknown>,
): string[] {
  const schema = fieldsSchema(manifest, typeName)
  if (!schema) return []
  const arrangement = arrangementsOf(schema, schema).find((entry) => entry.kind === 'list')
  return arrangement ? linkIdsIn(arrangement, fields) : []
}

